import {ProductDataType} from '../types/product'
import {normalizeProductUrl} from '../utils/urlUtils'

// Canonical output schema: list of all product fields we want in the output
const productViewModelFields = [
    'id',
    'name',
    'image_url',
    'number_of_reviews',
    'rating',
    'url',
    'variation_id',
    'price',
    'inventory',
    'brand',
    'longDescription',
    'shortDescription',
    'manufacturerName',
    'manufacturerSku',
    'currency',
    'priceMax',
    'pricePerUnit',
    'pricePerUnitMax',
    'priceRanges',
    'prices',
    'imageGroups',
    'variationAttributes',
    'variationValues',
    'variants',
    'variationGroups',
    'master',
    'c_priceInfo',
    'c_compareToSKU',
    'c_plpPromotions',
    'c_plpPromotionsDisplayFrom',
    'c_plpPromotionsDisplayTo',
    'c_badging',
    'c_secondBadging',
    'c_badgeDisplayFrom',
    'c_badgeDisplayTo',
    'c_secondBadgeDisplayFrom',
    'c_secondBadgeDisplayTo',
    'type',
    'minOrderQuantity',
    'primaryCategoryId',
    'recommendations',
    'stepQuantity',
    'upc',
    'c_Assortment_POD',
    'c_Assortment_Rank',
    'c_Assortment_Status',
    'c_Category',
    'c_Sub_Category',
    'c_allowReviews',
    'c_averageRating',
    'c_cardinalWarehouseSKU',
    'c_count',
    'c_customCarousalPosition',
    'c_disablePurchase',
    'c_eligibilityTypeID',
    'c_excludeFromFeed',
    'c_hazmat',
    'c_hybridCustomCarousel',
    'c_ingredients',
    'c_instructions',
    'c_isCaringMill',
    'c_isEligible',
    'c_isNew',
    'c_primaryCarousalPosition',
    'c_productCost',
    'c_productCostResetDate',
    'c_productDetails',
    'c_productType',
    'c_secondaryCarousalPosition',
    'c_shipsFrom',
    'c_siteLongDescription',
    'c_siteShortDescription',
    'c_totalReviews',
    'c_warehouseID',
    'c_warnings',
    'c_weight',
    'c_brandURL',
    'c_ownerClass',
    'slugUrl'
]

// Per-source mapping objects: map output field to input path for each source
const sourceFieldMappings: Record<string, Record<string, string[]>> = {
    constructor: {
        id: ['data.id', 'id'],
        name: ['value', 'data.name'],
        image_url: ['data.image_url'],
        url: ['data.url'],
        price: ['data.price'],
        number_of_reviews: ['data.totalReviews'],
        rating: ['data.averageRating'],
        variation_id: ['data.variants.0.variation_id']
        // Add more facet-based fields here as needed, only if they exist in constructor facets
    },
    scapi: {
        id: ['id'],
        name: ['name'],
        bundledProducts: ['bundledProducts'],
        image_url: ['imageGroups.0.images.0.link'],
        url: ['slugUrl'],
        price: ['price'],
        brand: ['brand', 'manufacturerName'],
        number_of_reviews: ['c_totalReviews'],
        rating: ['c_averageRating'],
        c_badging: ['c_badging'],
        c_secondBadging: ['c_secondBadging'],
        c_badgeDisplayFrom: ['c_badgeDisplayFrom'],
        c_badgeDisplayTo: ['c_badgeDisplayTo'],
        c_secondBadgeDisplayFrom: ['c_secondBadgeDisplayFrom'],
        c_secondBadgeDisplayTo: ['c_secondBadgeDisplayTo'],
        longDescription: ['c_siteLongDescription'],
        shortDescription: ['c_siteShortDescription'],
        inventory: ['inventory'],
        manufacturerName: ['manufacturerName'],
        manufacturerSku: ['manufacturerSku'],
        currency: ['currency'],
        priceMax: ['priceMax'],
        pricePerUnit: ['pricePerUnit'],
        pricePerUnitMax: ['pricePerUnitMax'],
        priceRanges: ['priceRanges'],
        prices: ['prices'],
        imageGroups: ['imageGroups'],
        variationAttributes: ['variationAttributes'],
        variationValues: ['variationValues'],
        variants: ['variants'],
        variationGroups: ['variationGroups'],
        master: ['master'],
        c_priceInfo: ['c_priceInfo'],
        c_compareToSKU: ['c_compareToSKU'],
        c_plpPromotions: ['c_plpPromotions'],
        c_plpPromotionsDisplayFrom: ['c_plpPromotionsDisplayFrom'],
        c_plpPromotionsDisplayTo: ['c_plpPromotionsDisplayTo'],
        type: ['type'],
        minOrderQuantity: ['minOrderQuantity'],
        primaryCategoryId: ['primaryCategoryId'],
        recommendations: ['recommendations'],
        stepQuantity: ['stepQuantity'],
        upc: ['upc'],
        c_Assortment_POD: ['c_Assortment_POD'],
        c_Assortment_Rank: ['c_Assortment_Rank'],
        c_Assortment_Status: ['c_Assortment_Status'],
        c_Category: ['c_Category'],
        c_Sub_Category: ['c_Sub_Category'],
        c_allowReviews: ['c_allowReviews'],
        c_averageRating: ['c_averageRating'],
        c_cardinalWarehouseSKU: ['c_cardinalWarehouseSKU'],
        c_count: ['c_count'],
        c_customCarousalPosition: ['c_customCarousalPosition'],
        c_disablePurchase: ['c_disablePurchase'],
        c_eligibilityTypeID: ['c_eligibilityTypeID'],
        c_excludeFromFeed: ['c_excludeFromFeed'],
        c_hazmat: ['c_hazmat'],
        c_hybridCustomCarousel: ['c_hybridCustomCarousel'],
        c_ingredients: ['c_ingredients'],
        c_instructions: ['c_instructions'],
        c_isCaringMill: ['c_isCaringMill'],
        c_isEligible: ['c_isEligible'],
        c_isNew: ['c_isNew'],
        c_primaryCarousalPosition: ['c_primaryCarousalPosition'],
        c_productCost: ['c_productCost'],
        c_productCostResetDate: ['c_productCostResetDate'],
        c_productDetails: ['c_productDetails'],
        c_productType: ['c_productType'],
        c_secondaryCarousalPosition: ['c_secondaryCarousalPosition'],
        c_shipsFrom: ['c_shipsFrom'],
        c_siteLongDescription: ['c_siteLongDescription'],
        c_siteShortDescription: ['c_siteShortDescription'],
        c_totalReviews: ['c_totalReviews'],
        c_warehouseID: ['c_warehouseID'],
        c_warnings: ['c_warnings'],
        c_weight: ['c_weight'],
        c_brandURL: ['c_brandURL'],
        c_ownerClass: ['c_ownerClass'],
        slugUrl: ['slugUrl']
    }
}
// Utility: Parse brand if stringified (constructor)

// Utility: Extract value from facets array by facet name
type Facet = {name: string; values: unknown[]}
function getFacetValue(facets: unknown, facetName: string): unknown {
    if (!Array.isArray(facets)) return undefined
    const facet = (facets as Facet[]).find((f) => f.name === facetName)
    if (!facet) return undefined
    return facet.values && facet.values.length === 1 ? facet.values[0] : facet.values
}

// Utility: Deep get by path (no lodash dependency)
function getValueFromPath(obj: Record<string, unknown>, path: string): unknown {
    return path
        .split('.')
        .reduce<unknown>(
            (o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined),
            obj
        )
}

function isBadgeScheduled(scheduleFrom: Date | null, scheduleTo: Date | null): boolean {
    const now = new Date()
    if (scheduleFrom && scheduleTo) return now >= scheduleFrom && now <= scheduleTo
    if (scheduleTo) return now <= scheduleTo
    if (scheduleFrom) return now >= scheduleFrom
    return true
}

function parseDateOrNull(val: unknown): Date | null {
    if (!val) return null
    const date = new Date(val as string)
    return isNaN(date.getTime()) ? null : date
}

// Helper: Assign defaults for missing fields
function assignDefaults<T extends object>(target: T, defaults: Partial<T>): T {
    for (const key in defaults) {
        if (!(key in target)) {
            ;(target as Record<string, unknown>)[key] = defaults[key]
        }
    }
    return target
}

// Helper: Extract a field value from prioritized sources and mappings
function extractFieldValue(
    field: string,
    sources: Array<Record<string, unknown> & {sourceType: string}>,
    mappings: Record<string, Record<string, string[]>>
): unknown {
    for (const source of sources) {
        const mapping = mappings[source.sourceType]?.[field]
        if (!mapping) continue
        for (const path of mapping) {
            if (source.sourceType === 'constructor') {
                if (field === 'brand') {
                    const facets = getValueFromPath(source, 'data.facets')
                    const value = getFacetValue(facets, 'brand')
                    if (value !== undefined) return value
                } else if (['c_badging', 'c_secondBadging'].includes(field)) {
                    const value = getFacetValue(
                        getValueFromPath(source, path),
                        field === 'c_badging' ? 'badging' : 'secondBadging'
                    )
                    if (value !== undefined) return value
                } else if (['longDescription', 'shortDescription'].includes(field)) {
                    const value = getFacetValue(
                        getValueFromPath(source, path),
                        field === 'longDescription' ? 'longDescription' : 'shortDescription'
                    )
                    if (value !== undefined) return value
                } else if (field === 'variation_id') {
                    let value = getValueFromPath(source, path)
                    if (value === undefined) {
                        const variations = getValueFromPath(source, 'variations')
                        if (Array.isArray(variations) && variations.length > 0) {
                            value = getValueFromPath(variations[0], 'data.variation_id')
                        }
                    }
                    if (value !== undefined) return value
                } else {
                    const value = getValueFromPath(source, path)
                    if (value !== undefined) return value
                }
            } else {
                const value = getValueFromPath(source, path)
                if (value !== undefined) return value
            }
        }
    }
    return undefined
}

// Schemas for different sources

/**
 * Builds a Product View Model for form usage, agnostic of input sources.
 * @param sources Array of product sources, each with a sourceType property
 * @param primarySourceType Which source type to use as the main mapping (e.g., 'constructor', 'scapi')
 */

export function createProductViewModel(
    sources: Array<Record<string, unknown> & {sourceType: string}>,
    primarySourceType: 'constructor' | 'scapi' = 'constructor'
): ProductDataType {
    const mappings = sourceFieldMappings
    // Prioritize sources by primary type
    const prioritizedSources = [
        ...sources.filter((s) => s.sourceType === primarySourceType),
        ...sources.filter((s) => s.sourceType !== primarySourceType)
    ]
    const output: Partial<ProductDataType> = {}
    for (const field of productViewModelFields) {
        const value = extractFieldValue(field, prioritizedSources, mappings)
        if (value !== undefined) {
            ;(output as Record<string, unknown>)[field] = value
        }
    }
    if (!output.id) {
        throw new Error('Product View Model must have an id.')
    }
    if (typeof output.url === 'string') {
        output.url = normalizeProductUrl(output.url)
    }
    // Strip badges outside their active schedule. Convention: 'c_[prefix]Badging' pairs with
    // 'c_[prefix]BadgeDisplayFrom' / 'c_[prefix]BadgeDisplayTo' (e.g. c_secondBadging → c_secondBadgeDisplayFrom).
    const outputRecord = output as Record<string, unknown>
    Object.keys(outputRecord).forEach((key) => {
        if (!key.toLowerCase().endsWith('badging')) return
        const base = key.replace(/([Bb])adging$/, '$1adgeDisplay')
        const from = parseDateOrNull(outputRecord[`${base}From`] as string)
        const to = parseDateOrNull(outputRecord[`${base}To`] as string)
        if (!isBadgeScheduled(from, to)) outputRecord[key] = ''
    })

    // Ensure all required ProductDataType fields are present with defaults if missing
    const requiredDefaults: Partial<ProductDataType> = {
        brand: '',
        longDescription: '',
        shortDescription: '',
        manufacturerName: '',
        manufacturerSku: '',
        currency: '',
        price: 9999,
        priceRanges: [],
        prices: {},
        image_url: '',
        imageGroups: [],
        variationAttributes: [],
        variationValues: {},
        variants: [],
        variationGroups: [],
        type: {},
        primaryCategoryId: '',
        productLinks: [],
        productPromotions: [],
        recommendations: [],
        setProducts: [],
        bundledProducts: [],
        options: [],
        inventories: [],
        inventory: {},
        rating: 9999,
        number_of_reviews: 0,
        url: '',
        variation_id: '',
        c_badging: '',
        c_secondBadging: '',
        c_plpPromotions: '',
        c_plpPromotionsDisplayFrom: '',
        c_plpPromotionsDisplayTo: '',
        c_isCaringMill: false,
        c_compareToSKU: '',
        c_youtubeID: '',
        c_priceInfo: {
            sales: {value: 9999, currency: '', formatted: '', decimalPrice: ''},
            list: null
        }
    }
    assignDefaults(output, requiredDefaults)
    return output as ProductDataType
}
